import { Badge } from "@bowfast/ui/components/badge";
import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bowfast/ui/components/card";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  ArrowLeftIcon,
  CalendarIcon,
  CompassIcon,
  MoreHorizontalIcon,
  Navigation2Icon,
  PenLineIcon,
  RulerIcon,
  SailboatIcon,
  UsersIcon,
  WrenchIcon,
} from "lucide-react";
import { useAuth } from "react-oidc-context";
import { DetailPageSkeleton } from "~/components/shared/DetailPageSkeleton";
import { ListLoadError } from "~/components/shared/ListLoadError";
import { StatTile } from "~/components/shared/StatTile";
import { ExpeditionRow } from "~/components/vessels/ExpeditionRow";
import { VesselActivityChart } from "~/components/vessels/VesselActivityChart";
import { VesselMetaChips } from "~/components/vessels/VesselMetaChips";
import { fadeUp, stagger } from "~/lib/animations";
import { createSdk } from "~/lib/gql";
import { gradientForId } from "~/lib/gradients";
import { SOURCE_CONFIG, STATUS_CONFIG, TYPE_LABEL } from "./vessels.config";
import type { VesselDetail } from "./vessels.types";
import { activeExpeditionName, berthsValue, buildSpecItems, makeModelSub } from "./vessels.utils";

// ── Detail view ───────────────────────────────────────────────────────────────

function VesselDetailView({ vessel: v }: Readonly<{ vessel: VesselDetail }>) {
  const gradient = gradientForId(v.id);
  const statusCfg = STATUS_CONFIG[v.status];
  const sourceCfg = SOURCE_CONFIG[v.source];
  const currentExpedition = activeExpeditionName(v);

  return (
    <div className="mx-auto w-full max-w-350 space-y-6 p-4 md:p-6">
      {/* ── Back nav ── */}
      <motion.div {...fadeUp}>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground"
          nativeButton={false}
          render={<Link to="/vessels" />}
        >
          <ArrowLeftIcon className="size-3.5" />
          Back to fleet
        </Button>
      </motion.div>

      {/* ── Hero card ── */}
      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.04 }}>
        <Card>
          <CardContent className="px-6 py-6">
            <div className="flex flex-col gap-5 md:flex-row md:items-start md:gap-6">
              <div
                className={`flex size-20 shrink-0 items-center justify-center rounded-2xl bg-linear-to-br p-0.5 ${gradient}`}
              >
                <div className="flex size-full items-center justify-center rounded-[13px] bg-abyss-900">
                  <SailboatIcon className="size-8 text-white/80" />
                </div>
              </div>

              <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h1 className="font-heading text-xl font-bold tracking-tight md:text-2xl">{v.name}</h1>
                    <Badge
                      variant={statusCfg.variant}
                      {...(statusCfg.dot ? { dotted: true } : {})}
                      {...(statusCfg.pulse ? { pulse: true } : {})}
                      className="text-[0.6875rem]"
                    >
                      {statusCfg.label}
                    </Badge>
                    <Badge variant={sourceCfg.variant} className="text-[0.6875rem]">
                      {sourceCfg.label}
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {v.make && v.model ? `${v.make} ${v.model} · ` : ""}
                    {v.vesselType ? `${TYPE_LABEL[v.vesselType]} · ` : ""}
                    {v.yearBuilt ? `Built ${v.yearBuilt}` : ""}
                  </p>
                  <VesselMetaChips
                    homePort={v.homePort}
                    flagState={v.flagState}
                    berths={v.berths}
                    maxPassengers={v.maxPassengers}
                    currentExpedition={currentExpedition}
                  />
                </div>

                <div className="flex shrink-0 gap-2">
                  <Button variant="outline" size="sm" className="gap-2">
                    <PenLineIcon className="size-3.5" />
                    Edit
                  </Button>
                  <Button size="sm" className="gap-2">
                    <SailboatIcon className="size-3.5" />
                    Assign voyage
                  </Button>
                  <Button variant="ghost" size="icon-sm">
                    <MoreHorizontalIcon className="size-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* ── Stats row ── */}
      <motion.div
        className="grid grid-cols-2 gap-4 lg:grid-cols-4"
        initial="initial"
        animate="animate"
        variants={stagger}
      >
        <motion.div variants={fadeUp}>
          <StatTile
            label="Expeditions"
            value={String(v.expeditionAssignments.length)}
            sub="voyages assigned"
            Icon={CompassIcon}
            iconColor="text-shallow-600"
            accentClass="bg-shallow-500"
          />
        </motion.div>
        <motion.div variants={fadeUp}>
          <StatTile
            label="Berths"
            value={berthsValue(v.berths, v.maxPassengers)}
            sub="crew / max passengers"
            Icon={UsersIcon}
            iconColor="text-signal-600"
            accentClass="bg-signal-500"
          />
        </motion.div>
        <motion.div variants={fadeUp}>
          <StatTile
            label="LOA"
            value={v.lengthM != null ? `${v.lengthM} m` : "—"}
            sub="length overall"
            Icon={Navigation2Icon}
            iconColor="text-abyss-500"
            accentClass="bg-abyss-500"
          />
        </motion.div>
        <motion.div variants={fadeUp}>
          <StatTile
            label="Year built"
            value={v.yearBuilt != null ? String(v.yearBuilt) : "—"}
            sub={makeModelSub(v.make, v.model)}
            Icon={CalendarIcon}
            iconColor="text-violet-500"
            accentClass="bg-violet-500"
          />
        </motion.div>
      </motion.div>

      {/* ── Two-column layout ── */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* ── Left column (2/3) ── */}
        <div className="space-y-5 lg:col-span-2">
          {/* Expedition history */}
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.16 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="font-heading text-[0.9375rem] font-semibold">Expedition History</CardTitle>
                <span className="text-xs text-muted-foreground">{v.expeditionAssignments.length} voyages</span>
              </CardHeader>
              <CardContent className="pt-0">
                {v.expeditionAssignments.length === 0 ? (
                  <p className="py-4 text-center text-sm text-muted-foreground">
                    No expeditions yet — assign this vessel to a voyage
                  </p>
                ) : (
                  <div className="divide-y divide-border">
                    {v.expeditionAssignments.map((ea) => (
                      <ExpeditionRow key={ea.id} ea={ea} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Expedition activity chart */}
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }}>
            <VesselActivityChart assignments={v.expeditionAssignments} />
          </motion.div>
        </div>

        {/* ── Right column (1/3) ── */}
        <div className="space-y-5">
          {/* Technical specs */}
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.18 }}>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-[0.9375rem] font-semibold">Technical Specs</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <dl className="space-y-3">
                  {buildSpecItems(v).map((item) => (
                    <div key={item.label} className="flex items-center justify-between gap-4">
                      <dt className="text-xs text-muted-foreground">{item.label}</dt>
                      <dd className="truncate text-right text-sm font-medium">{item.value}</dd>
                    </div>
                  ))}
                </dl>
              </CardContent>
            </Card>
          </motion.div>

          {/* Quick actions */}
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.22 }}>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-[0.9375rem] font-semibold">Actions</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-2 pt-0">
                <Button size="sm" className="w-full gap-2">
                  <SailboatIcon className="size-3.5" />
                  Assign to expedition
                </Button>
                <Button variant="outline" size="sm" className="w-full gap-2">
                  <WrenchIcon className="size-3.5" />
                  Log maintenance
                </Button>
                <Button variant="outline" size="sm" className="w-full gap-2">
                  <RulerIcon className="size-3.5" />
                  View logbook
                </Button>
                <Button variant="ghost" size="sm" className="w-full gap-2 text-destructive hover:text-destructive">
                  Remove from fleet
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────────────

export function VesselDetailPage() {
  const auth = useAuth();
  const { vesselSlug } = useParams({ strict: false }) as { vesselSlug: string };
  const token = auth.user?.access_token ?? "";

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ["vessel-detail", vesselSlug],
    queryFn: () => createSdk(token).VesselBySlug({ slug: vesselSlug }),
    enabled: !!token && !!vesselSlug,
    staleTime: 60 * 1000,
  });

  if (isPending) return <DetailPageSkeleton />;
  if (isError || !data?.vesselBySlug) {
    return (
      <ListLoadError
        title="Could not load vessel details."
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  return <VesselDetailView vessel={data.vesselBySlug} />;
}
